export interface OptimizedImage {
  blob: Blob;
  filename: string;
  width: number;
  height: number;
  originalBytes: number;
  optimizedBytes: number;
  savingPercent: number;
}

const MAX_INPUT_BYTES = 10 * 1024 * 1024;
const MAX_OUTPUT_BYTES = 500 * 1024;

function canvasBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error("Foto tidak dapat diproses.")), "image/webp", quality);
  });
}

export async function optimizeProductImage(file: File): Promise<OptimizedImage> {
  if (!/^image\/(jpeg|png|webp)$/.test(file.type)) throw new Error("Gunakan foto JPG, PNG, atau WebP.");
  if (file.size > MAX_INPUT_BYTES) throw new Error("Ukuran foto asli maksimal 10 MB.");
  const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  const canvas = document.createElement("canvas");
  canvas.width = 800;
  canvas.height = 800;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Browser tidak mendukung pemrosesan foto.");
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, 800, 800);
  const scale = Math.min(800 / bitmap.width, 800 / bitmap.height);
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);
  context.drawImage(bitmap, Math.round((800 - width) / 2), Math.round((800 - height) / 2), width, height);
  bitmap.close();

  let quality = 0.82;
  let blob = await canvasBlob(canvas, quality);
  while (blob.size > MAX_OUTPUT_BYTES && quality > 0.56) {
    quality -= 0.06;
    blob = await canvasBlob(canvas, quality);
  }
  if (blob.size > MAX_OUTPUT_BYTES) throw new Error("Foto masih terlalu besar. Silakan gunakan foto lain.");
  const baseName = file.name.replace(/\.[^.]+$/, "").replace(/[^a-zA-Z0-9-_]+/g, "-");
  return {
    blob,
    filename: `${baseName || "foto-produk"}.webp`,
    width: 800,
    height: 800,
    originalBytes: file.size,
    optimizedBytes: blob.size,
    savingPercent: Math.max(0, Math.round((1 - blob.size / file.size) * 1000) / 10),
  };
}

export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Foto tidak dapat dibaca."));
    reader.onload = () => {
      if (typeof reader.result !== "string") {
        reject(new Error("Foto tidak dapat diubah menjadi data upload."));
        return;
      }
      resolve(reader.result.split(",")[1] ?? "");
    };
    reader.readAsDataURL(blob);
  });
}
